import React, { useState, useEffect } from 'react';
import { FaShoppingCart, FaPlus, FaEdit, FaTrash, FaSearch, FaImage, FaTimes, FaShoppingBag, FaEye } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { useToast } from '../../hooks/useToast';
import LoadingSpinner from '../common/LoadingSpinner';
import Modal from '../common/Modal';
import { formatCurrency } from '../../utils/helpers';

/**
 * Ecommerce management component for schools
 * Allows managing products in the school's online shop
 */
const EcommerceManagement = () => {
  const { showToast, showSuccess, showError } = useToast();

  // Products state
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState('add'); // 'add' or 'edit'
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    description: '',
    category: '',
    inStock: true,
    quantity: '',
    image: null,
    imageURL: '',
    featured: false
  });
  
  // Form errors
  const [formErrors, setFormErrors] = useState({});

  // Format category for display
  const formatCategory = (category) => {
    if (!category) return '';
    
    // Replace underscores with spaces and capitalize each word
    return category
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Fetch products on component mount
  useEffect(() => {
    fetchProducts();
  }, []);

  // Fetch products from API
  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/ecommerce/products');
      setProducts(response.data.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      showError('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  // Filter products based on search term
  const filteredProducts = searchTerm
    ? products.filter(
        product =>
          product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.category.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : products;

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' 
        ? checked 
        : type === 'number' 
          ? (value === '' ? '' : parseFloat(value)) 
          : value
    }));
    
    // Clear the error for this field if there was one
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle image upload
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          image: file,
          imageURL: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Open add form
  const handleAddNew = () => {
    setFormData({
      name: '',
      price: '',
      description: '',
      category: '',
      inStock: true,
      quantity: '',
      image: null,
      imageURL: '',
      featured: false
    });
    setFormMode('add');
    setShowForm(true);
    setFormErrors({});
  };

  // Open edit form
  const handleEdit = (product) => {
    setSelectedProduct(product);
    setFormData({
      name: product.name || '',
      price: product.price || '',
      description: product.description || '',
      category: product.category || '',
      inStock: product.inStock !== false, // default to true if undefined
      quantity: product.quantity || '',
      image: null,
      imageURL: product.imageURL || '',
      featured: product.featured || false
    });
    setFormMode('edit');
    setShowForm(true);
    setFormErrors({});
  };

  // Open delete confirmation
  const handleDeleteClick = (product) => {
    setSelectedProduct(product);
    setShowDeleteModal(true);
  };

  // Delete product
  const handleDeleteConfirm = async () => {
    if (!selectedProduct) return;
    
    try {
      await api.delete(`/ecommerce/products/${selectedProduct._id}`);
      showSuccess('Product deleted successfully');
      setShowDeleteModal(false);
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      showError('Failed to delete product');
    }
  };

  // Form validation
  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Product name is required';
    }
    
    if (!formData.price || formData.price <= 0) {
      errors.price = 'Valid price is required';
    }
    
    if (!formData.category.trim()) {
      errors.category = 'Category is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Prepare form data
    const productData = new FormData();
    
    // Add required fields
    productData.append('name', formData.name);
    productData.append('price', formData.price);
    productData.append('category', formData.category);
    
    // Add description fields (required in the model)
    productData.append('description', formData.description || `${formData.name} - ${formatCategory(formData.category)}`);
    productData.append('shortDescription', formData.description?.slice(0, 200) || `${formData.name} - School Product`);
    
    // Add product type and default status
    productData.append('productType', 'physical');
    productData.append('status', formData.inStock ? 'published' : 'out_of_stock');
    
    // Add inventory information using flat fields
    // (The controller will convert these to the proper structure)
    productData.append('quantity', formData.quantity || '0');
    productData.append('allowBackorder', 'false');
    productData.append('lowStockThreshold', '5');
    
    // Add other optional fields
    if (formData.featured !== undefined) {
      productData.append('featured', formData.featured);
    }
    
    // Add image if present
    if (formData.image) {
      productData.append('image', formData.image);
    }
    
    try {
      if (formMode === 'add') {
        const response = await api.post('/ecommerce/products', productData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        console.log('Product created successfully:', response.data);
        showSuccess('Product added successfully');
      } else {
        const response = await api.put(`/ecommerce/products/${selectedProduct._id}`, productData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        console.log('Product updated successfully:', response.data);
        showSuccess('Product updated successfully');
      }
      
      setShowForm(false);
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      const errorDetail = 
        error.response?.data?.error || 
        error.response?.data?.message || 
        error.message || 
        'Failed to save product';
      
      console.error('Server error details:', error.response?.data);
      showError(`Error: ${errorDetail}`);
    }
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <FaShoppingCart className="mr-2 text-primary" /> Shop Management
          </h1>
          <p className="text-gray-600 mt-1">
            Manage your school's online shop products
          </p>
        </div>
        <div className="mt-4 md:mt-0 flex space-x-3">
          <Link
            to="/school/ecommerce/orders"
            className="btn-secondary flex items-center"
          >
            <FaShoppingBag className="mr-2" /> Manage Orders
          </Link>
          <Link
            to="/school/ecommerce/preview"
            className="btn-secondary flex items-center"
          >
            <FaEye className="mr-2" /> Preview Shop
          </Link>
          <button
            onClick={handleAddNew}
            className="btn-primary flex items-center"
          >
            <FaPlus className="mr-2" /> Add New Product
          </button>
        </div>
      </div>
      
      {/* Search Bar */}
      <div className="bg-white rounded-lg shadow-soft p-6 mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search products by name, description, or category..."
            className="form-input pl-10 py-2 w-full"
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FaSearch className="text-gray-400" />
          </div>
        </div>
      </div>
      
      {/* Products Grid */}
      {loading ? (
        <div className="bg-white rounded-lg shadow-soft p-8 flex justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-soft p-6">
          {filteredProducts.length === 0 ? (
            <div className="text-center py-8">
              <FaShoppingCart className="mx-auto text-gray-300 text-5xl mb-4" />
              <h3 className="text-xl font-medium text-gray-600 mb-2">
                {searchTerm ? 'No products match your search' : 'No Products Added Yet'}
              </h3>
              <p className="text-gray-500 mb-6">
                {searchTerm 
                  ? 'Try adjusting your search terms' 
                  : 'Add products to your school shop to get started'}
              </p>
              {!searchTerm && (
                <button
                  onClick={handleAddNew}
                  className="btn-primary flex items-center mx-auto"
                >
                  <FaPlus className="mr-2" /> Add Product
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredProducts.map(product => (
                <div 
                  key={product._id} 
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <div className="relative h-40 bg-gray-100">
                    {product.imageURL ? (
                      <img 
                        src={product.imageURL} 
                        alt={product.name} 
                        className="w-full h-full object-contain p-2"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FaShoppingCart className="text-gray-400 text-4xl" />
                      </div>
                    )}
                    {!product.inStock && (
                      <div className="absolute inset-0 bg-black bg-opacity-60 flex items-center justify-center">
                        <span className="text-white font-medium px-3 py-1 bg-red-500 rounded">Out of Stock</span>
                      </div>
                    )}
                    {product.featured && (
                      <div className="absolute top-0 left-0 bg-yellow-500 text-white px-2 py-1 text-xs">
                        Featured
                      </div>
                    )}
                  </div>
                  
                  <div className="p-4">
                    <h3 className="font-medium text-gray-900 mb-1">{product.name}</h3>
                    <div className="text-sm text-gray-500 mb-2">{formatCategory(product.category)}</div>
                    <div className="font-bold mb-3">{formatCurrency(product.price)}</div>
                    
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(product)}
                        className="flex-1 btn-primary-outline py-1 text-sm"
                      >
                        <FaEdit className="inline mr-1" /> Edit
                      </button>
                      <button
                        onClick={() => handleDeleteClick(product)}
                        className="flex-1 btn-danger-outline py-1 text-sm"
                      >
                        <FaTrash className="inline mr-1" /> Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Add/Edit Product Form */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-medium">
                {formMode === 'add' ? 'Add New Product' : 'Edit Product'}
              </h3>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <FaTimes />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Product Name*</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`form-input w-full ${formErrors.name ? 'border-red-500' : ''}`}
                    />
                    {formErrors.name && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>
                    )}
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Price (KES)*</label>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      step="0.01"
                      min="0"
                      className={`form-input w-full ${formErrors.price ? 'border-red-500' : ''}`}
                    />
                    {formErrors.price && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.price}</p>
                    )}
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Category*</label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      className={`form-select w-full ${formErrors.category ? 'border-red-500' : ''}`}
                    >
                      <option value="">Select Category</option>
                      <option value="books">Books</option>
                      <option value="uniforms">Uniforms</option>
                      <option value="stationery">Stationery</option>
                      <option value="sports_equipment">Sports Equipment</option>
                      <option value="digital_resources">Digital Resources</option>
                      <option value="learning_tools">Learning Tools</option>
                      <option value="educational_toys">Educational Toys</option>
                      <option value="art_supplies">Art Supplies</option>
                      <option value="other">Other</option>
                    </select>
                    {formErrors.category && (
                      <p className="text-red-500 text-xs mt-1">{formErrors.category}</p>
                    )}
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Description</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows="4"
                      className="form-textarea w-full"
                    ></textarea>
                  </div>
                </div>
                
                <div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Product Image</label>
                    <div className="border border-dashed border-gray-300 rounded-lg p-4 text-center">
                      {formData.imageURL ? (
                        <div className="mb-3">
                          <img 
                            src={formData.imageURL} 
                            alt="Product preview" 
                            className="max-h-40 mx-auto"
                          />
                        </div>
                      ) : (
                        <FaImage className="text-gray-300 text-5xl mx-auto mb-3" />
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                        id="product-image"
                      />
                      <label 
                        htmlFor="product-image" 
                        className="btn-secondary text-sm cursor-pointer"
                      >
                        Select Image
                      </label>
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Quantity in Stock</label>
                    <input
                      type="number"
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleChange}
                      min="0"
                      className="form-input w-full"
                    />
                  </div>
                  
                  <div className="flex items-center mb-4">
                    <input
                      type="checkbox"
                      id="inStock"
                      name="inStock"
                      checked={formData.inStock}
                      onChange={handleChange}
                      className="form-checkbox mr-2"
                    />
                    <label htmlFor="inStock">Product is in stock</label>
                  </div>
                  
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="featured"
                      name="featured"
                      checked={formData.featured}
                      onChange={handleChange}
                      className="form-checkbox mr-2"
                    />
                    <label htmlFor="featured">Feature this product</label>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6 border-t border-gray-200 pt-6">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                >
                  {formMode === 'add' ? 'Add Product' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Confirm Deletion"
        size="sm"
      >
        <div className="p-6">
          <p className="mb-4">
            Are you sure you want to delete the product "{selectedProduct ? selectedProduct.name : ''}"?
            This action cannot be undone.
          </p>
          
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowDeleteModal(false)}
              className="btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteConfirm}
              className="btn-danger"
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default EcommerceManagement;
